import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getStringWithoutHash } from 'utilitys/helpers/general';

import useCustomPageSubTitle from 'hooks/useCustomPageSubTitle';
import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';
import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';
import { useQueryState } from 'hooks/useQueryState';
import useTableCurrentState from 'hooks/useTableCurrentState';

import { useConfigurationStore } from 'store/configurationsStore';

import { useMutationSetWidgetColumns } from 'api/mutations/setWidgetColumns.mutation';
import { useQueryTopDurations } from 'api/queries/getTopDuration.query';

import { ColumnsManager } from 'components/common/ColumnsManager/ColumnsManager';
import ErrorBoundary from 'components/common/ErrorBoundary/ErrorBoundary';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import {
    FilterNames,
    filterMapper,
    getFiltersData,
} from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';
import { List } from 'components/common/ListWrapper/list';
import MainTable from 'components/common/Tables/MainTable/MainTable';
import { ColumnCommonConfig } from 'components/common/Tables/columnsCommonConfig';
import { TableContainer } from 'components/common/Tables/components/TableContainer/TableContainer';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';

import { statusOptions } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProgramCriticalAnalysis/criticalAnalysisDelayDriversFilter';
import { morefilterItems } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProgramCriticalAnalysis/utils';
import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/style/table.module.scss';
import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { isBetweenRange } from 'components/Dashboards/Project/Components/helper';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { Select } from 'components/DesignSystem/Select';
import { InputOption } from 'components/DesignSystem/Select/components/InputOption/InputOption';
import { ValueContainer } from 'components/DesignSystem/Select/components/ValueContainer/ValueContainer';
import { Switch } from 'components/DesignSystem/Switch';
import {
    TOP_OVERDUE_OVERRUN_FILTERS,
    columns,
    defaultPeriod,
    initialState,
} from 'components/widgets/TopDurationOverrunRisk/topDurationOverrunRisk.utils';

const groupByOptions: ISelectOption<ColumnCommonConfig>[] = [
    { value: 'clusterName', label: 'Cluster' },
    { value: 'parentName', label: 'WBS' },
];

export const RenderTopDurationOverrunRisk = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    filter,
    localFilters,
    globalFilterData,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
}: IComponentProps) => {
    const { componentKey, routeId: id, name: configTitle } = useConfigurationStore().topDurationOverrunRisk;

    const [isInstance, setIsInstance] = useState(false);
    const tableRef = useRef(null);
    const route = `${getStringWithoutHash(window.location.href)}#${id}`;
    const subTitle = useCustomPageSubTitle({ projectId, contractId, latestVersionId });
    const [groupBy, setGroupBy] = useState<readonly ISelectOption<ColumnCommonConfig>[]>([groupByOptions[0]]);
    const mergedWithGroupBy = useMemo(() => {
        return { ...initialState, groupBy: groupBy.map((item) => item.value) };
    }, [groupBy]);
    const updatedInitialState = useTableCurrentState({
        initialState: mergedWithGroupBy,
        componentKey,
        widgetId,
    });

    const [modalColumnsOpen, setModalColumnsOpen] = useState<boolean>(false);
    const idList = useMemo(() => (latestVersionId ? [latestVersionId] : []), [latestVersionId]);
    const [status, setStatus] = useState<readonly ISelectOption<string>[]>([]);
    const [isCriticalPath, setIsCriticalPath] = useState<boolean>(false);
    const [statusFilter, setStatusFilter] = useState<readonly ISelectOption<string>[]>([
        {
            value: 'TODO',
            label: 'To Do',
        },
        {
            value: 'IN_PROGRESS',
            label: 'In Progress',
        },
    ]);
    const [nextFilter, setNextFilter] = useQueryState('nextFilter');

    const numOfMonthsToBeSelected: string = useMemo(() => {
        return widgetId && localFilters?.numOfMonths ? localFilters.numOfMonths.value : nextFilter;
    }, [widgetId, localFilters, nextFilter]);

    const [numOfMonths, setNumOfMonths] = useState<ISelectOption<string> | undefined>(undefined);

    const filters = useMemo(
        () =>
            widgetId
                ? filter?.filters
                : getFiltersData({
                      [FilterNames.ACTIVITY_CODES]: globalFilterData?.activityCodes
                          ?.filter((item) => !item.isExclude)
                          .map((i) => i.name),
                      [FilterNames.ACTIVITY_CODES_EXCLUDE]: globalFilterData?.activityCodes
                          ?.filter((item) => item.isExclude)
                          .map((i) => i.name),
                      [FilterNames.FRAGNET_SEARCH]: globalFilterData?.milestoneFragnet,
                      [FilterNames.TAG_CARD_BOARD]: globalFilterData?.tags
                          ?.filter((item) => !item.isExclude)
                          .map((i) => i.name),
                      [FilterNames.TAG_CARD_BOARD_EXCLUDE]: globalFilterData?.tags
                          ?.filter((item) => item.isExclude)
                          .map((i) => i.name),
                      [FilterNames.WBS_SEARCH]: globalFilterData?.wbs,
                      [FilterNames.OPTIONS_STATUS]: statusFilter.map((item) => item.value).map(filterMapper),
                      [FilterNames.OPTIONS_TYPE]: isCriticalPath
                          ? [...status.map((item) => item.value).map(filterMapper), 'CRITICAL_PATH']
                          : status.map((item) => item.value).map(filterMapper),
                  }),
        [filter, globalFilterData, statusFilter, status, isCriticalPath],
    );
    const { data, isLoading, isFetching } = useQueryTopDurations({
        id: idList,
        filter: {
            filters,
        },
    });

    const mergedFilters =
        widgetId && filter?.filters
            ? {
                  filters: [
                      ...filter?.filters,
                      ...getFiltersData({
                          [FilterNames.OVERRUN_RISK_SELECTED_PERIOD]:
                              Number(localFilters?.numOfMonths?.value) > 0
                                  ? [`${localFilters?.numOfMonths?.value}M`]
                                  : ['All'],
                      }),
                  ],
              }
            : undefined;

    const { generateFilters: formatedFilter } = useGlobalFiltersHook({
        filter: mergedFilters,
        latestVersionId,
    });
    const { sendEventWithDimensions } = useEventWithDimensions();
    const { handleSnackBar } = useCustomSnackBar();
    const { mutate: mutateSetWidgetColumns } = useMutationSetWidgetColumns();

    const handleStatus = useCallback((value: readonly ISelectOption<string>[]) => {
        const selected = value.map((item) => item.value);
        setStatus(value);

        sendEventWithDimensions({
            category: 'Activities Table(s)',
            action: 'Status Filter click',
            label: selected.length > 0 ? selected.join(',') : 'Deselect All',
        });
    }, []);

    const filteredRows = useMemo(() => {
        return data?.delayDrivers
            ? data.delayDrivers.filter((item) =>
                  isBetweenRange({
                      numOfMonths: Number(numOfMonthsToBeSelected),
                      startDate: item.currentStartDate,
                      endDate: item.currentFinishDate,
                  }),
              )
            : [];
    }, [data, numOfMonthsToBeSelected]);

    const handleStatusType = (value: readonly ISelectOption<string>[]) => {
        setStatusFilter(value);
    };

    const handleInstance = (instance) => {
        if (instance) {
            tableRef.current = instance;
            setIsInstance(true); // need for render component after getting instance
        }
    };
    const handleTimePeriodClick = (filter: ISelectOption<string>) => {
        const value = filter.value;
        sendEventWithDimensions({
            category: 'Activities Table(s)',
            action: 'Next Selection',
            label: value,
        });
        setNextFilter(value);
        setNumOfMonths(filter);
    };
    const setWidgetColumns = (widgetId: string) => {
        if (tableRef.current) {
            const { hiddenColumns, columnOrder } = tableRef.current.state;
            mutateSetWidgetColumns(
                { columns: { hiddenColumns, columnOrder }, widgetId },
                {
                    onError: (error) => {
                        DEFAULT_ERROR({ error, handleError: handleSnackBar });
                    },
                },
            );
        }
    };
    useEffect(() => {
        setSubTitleForExport &&
            setSubTitleForExport({ widgetId, subTitle, title: configTitle || 'Top Duration Overrun Risk' });
    }, [widgetId, subTitle, configTitle]);

    useEffect(() => {
        if (!nextFilter && !widgetId) {
            setNextFilter(defaultPeriod);
        }
        if (nextFilter && !widgetId) {
            const defaultNumOfMonths = TOP_OVERDUE_OVERRUN_FILTERS.find(
                (filter) => filter.value === numOfMonthsToBeSelected,
            );
            setNumOfMonths(defaultNumOfMonths);
        }
    }, [nextFilter, widgetId, numOfMonthsToBeSelected]);
    useEffect(() => {
        if (tableRef?.current) {
            tableRef.current.setGroupBy(groupBy.map((item) => item.value));
        }
    }, [groupBy]);
    const handleCriticalPath = (event) => {
        const isChecked = event.target.checked;
        setIsCriticalPath(isChecked);
    };
    return (
        <ErrorBoundary>
            <WidgetWithTitle
                id={id}
                title={<WidgetTitle title={configTitle || 'Top Duration Overrun Risk'} />}
                titleComponents={[
                    externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                    <AddWidget
                        key={'AddWidget'}
                        componentKey={componentKey}
                        title={configTitle || 'Top Duration Overrun Risk'}
                        projectId={projectId}
                        contractId={contractId}
                        widgetId={widgetId}
                        route={route}
                        filters={filters}
                        localFilters={{ numOfMonths }}
                        callBack={setWidgetColumns}
                        shouldHide={true}
                    />,
                    isInstance && (
                        <ExportDropdown
                            title={configTitle || 'Top Duration Overrun Risk'}
                            data={filteredRows}
                            tableInstance={tableRef.current}
                            subTitle={subTitle}
                        />
                    ),
                    tableRef?.current && (
                        <ColumnsManager
                            onOpenModal={() => setModalColumnsOpen(true)}
                            onHideModal={() => setModalColumnsOpen(false)}
                            showModal={modalColumnsOpen}
                            widgetId={widgetId}
                            componentKey={componentKey}
                            tableInstance={tableRef.current}
                            initialState={initialState}
                            columns={columns}
                        />
                    ),
                    <TooltipIcon key={'info'} tooltip={data?.info} />,
                    <ThreeDotsMenu
                        widgetId={widgetId}
                        componentKey={componentKey}
                        shareTitle={configTitle || 'Top Duration Overrun Risk'}
                        shareLink={route}
                    />,
                ]}
                titleFilters={[
                    editNarrative && <div key={'editNarrative'}>{editNarrative}</div>,
                    widgetId && formatedFilter.length > 0 ? (
                        <PinboardWidgetPills key={'widgetFilters'} filters={formatedFilter} />
                    ) : (
                        <div key={'filters'} className={classes.filters}>
                            <div className={classes.selectWrapper}>
                                <div className={classes.title}>Group By</div>
                                <Select<ISelectOption<ColumnCommonConfig>, true>
                                    value={groupBy}
                                    options={groupByOptions}
                                    isMulti={true}
                                    onChange={(value) => setGroupBy(value)}
                                    placeholder={'Group by...'}
                                />
                            </div>
                            <div className={classes.selectWrapper}>
                                <div className={classes.title}>Status</div>
                                <Select<ISelectOption<string>, true>
                                    value={statusFilter}
                                    options={statusOptions}
                                    onChange={handleStatusType}
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    isMulti={true}
                                    placeholder={'Select Status...'}
                                    components={{
                                        Option: InputOption,
                                        ValueContainer: ValueContainer,
                                    }}
                                />
                            </div>
                            <div>
                                <Switch
                                    id={'switch-advanced'}
                                    onChange={handleCriticalPath}
                                    checked={isCriticalPath}
                                    labelPlacement={'start'}
                                    label={'Critical Path Only'}
                                />
                            </div>
                            <div className={classes.selectWrapper}>
                                <div className={classes.title}>More filters</div>
                                <Select<ISelectOption<string>, true>
                                    value={status}
                                    options={morefilterItems}
                                    onChange={handleStatus}
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    isMulti={true}
                                    placeholder={'Select More Filters...'}
                                    components={{
                                        Option: InputOption,
                                        ValueContainer: ValueContainer,
                                    }}
                                />
                            </div>

                            <div className={classes.selectWrapper}>
                                <div className={classes.title}>Next</div>
                                <div className={classes.filtersWrapper}>
                                    <List<string>
                                        onChange={handleTimePeriodClick}
                                        options={TOP_OVERDUE_OVERRUN_FILTERS}
                                        value={numOfMonths}
                                    />
                                </div>
                            </div>
                        </div>
                    ),
                ]}
            >
                <TableContainer>
                    {updatedInitialState && (
                        <MainTable
                            getInstanceCallback={handleInstance}
                            columns={columns}
                            data={filteredRows}
                            initialState={updatedInitialState}
                            pageSize={1000}
                            isLoading={isLoading || isFetching}
                        />
                    )}
                </TableContainer>
            </WidgetWithTitle>
        </ErrorBoundary>
    );
};
